import { useEffect } from "react";
import { HardHat, Users, Clock, Award, UserCheck, Shield } from 'lucide-react';

function ConstructionPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Skilled Workforce",
      description: "Access to qualified construction professionals, from laborers to project managers"
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Safety First",
      description: "All personnel trained in safety protocols and compliance standards"
    },
    {
      icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Flexible Deployment",
      description: "Quick mobilization and scalable workforce for projects of any size"
    },
    {
      icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Quality Assurance",
      description: "Thoroughly vetted professionals ensuring project excellence"
    }
  ];

  const roles = [
    {
      title: "Construction Laborers",
      responsibilities: ["Site preparation", "Material handling", "General construction tasks", "Equipment operation"]
    },
    {
      title: "Site Engineers",
      responsibilities: ["Project planning", "Site supervision", "Quality control", "Progress monitoring"]
    },
    {
      title: "Project Managers",
      responsibilities: ["Overall project coordination", "Timeline management", "Budget oversight", "Client communication"]
    },
    {
      title: "Safety Officers",
      responsibilities: ["Safety compliance", "Risk assessment", "Training coordination", "Incident management"]
    },
    {
      title: "Skilled Tradesmen",
      responsibilities: ["Specialized construction work", "Technical installations", "Quality craftsmanship", "Blueprint interpretation"]
    },
    {
      title: "Site Supervisors",
      responsibilities: ["Team coordination", "Daily operations", "Resource allocation", "Quality monitoring"]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Banner */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] w-full">
        <img
          src="/assest/section-showcase/architect-man-showing-something-project-his-colleague-foreman.jpg"
          alt="Construction Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        
        <div className="container relative z-10 h-full px-5 sm:px-6 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Construction <br />Manpower Solutions
            </h1>
            <p className="font-body text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-2xl">
              Building success with skilled professionals and comprehensive workforce solutions for your construction projects
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="section-label mb-3 sm:mb-4 inline-block text-xs sm:text-sm">
              Our Expertise
            </span>
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] lg:text-[52px] font-bold text-brand-navy leading-tight mb-6">
              Comprehensive Staffing for Construction Projects
            </h2>
            <p className="text-text-dark text-base sm:text-lg leading-relaxed mb-8">
              IMMACULATE provides skilled manpower solutions for the construction industry. From project managers 
              and site engineers to skilled laborers and safety officers, we supply qualified personnel who understand 
              the demands of construction projects. Our commitment to safety, quality, and timely delivery ensures 
              your projects stay on track and meet the highest standards of excellence.
            </p>
            <blockquote className="border-l-4 border-gold pl-6 my-8">
              <p className="font-display italic text-lg sm:text-xl text-text-medium leading-relaxed">
                "Quality is never an accident; it is always the result of intelligent effort and skilled execution."
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] font-bold text-brand-navy leading-tight">
              Why Choose Our Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white p-6 sm:p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-gold mb-4">
                  {benefit.icon}
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-brand-navy mb-3">
                  {benefit.title}
                </h3>
                <p className="text-text-dark text-sm sm:text-base leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <span className="section-label mb-3 sm:mb-4 inline-block text-xs sm:text-sm">
              Career Opportunities
            </span>
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] lg:text-[52px] font-bold text-brand-navy leading-tight mb-4">
              Available Positions
            </h2>
            <p className="text-text-dark text-base sm:text-lg max-w-3xl mx-auto">
              Discover diverse career opportunities in construction with competitive compensation 
              packages tailored to your experience and expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
            {roles.map((role, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-6">
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-brand-navy">
                    {role.title}
                  </h3>
                  <HardHat className="w-6 h-6 text-gold flex-shrink-0 ml-2" />
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-brand-navy text-sm uppercase tracking-wide mb-3">
                    Key Responsibilities:
                  </h4>
                  <ul className="space-y-2">
                    {role.responsibilities.map((resp, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-gold mr-2 mt-1">•</span>
                        <span className="text-text-dark text-sm sm:text-base">{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] font-bold text-brand-navy leading-tight text-center mb-10 sm:mb-12">
              Construction Specializations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <HardHat className="w-8 h-8 text-gold mr-3" />
                  <h3 className="font-display text-xl font-bold text-brand-navy">Residential</h3>
                </div>
                <ul className="space-y-2 text-text-dark">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Housing Projects</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Villas & Apartments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Renovation Works</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Interior Fit-Outs</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <UserCheck className="w-8 h-8 text-gold mr-3" />
                  <h3 className="font-display text-xl font-bold text-brand-navy">Commercial</h3>
                </div>
                <ul className="space-y-2 text-text-dark">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Office Buildings</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Retail Spaces</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Infrastructure Projects</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Industrial Facilities</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ConstructionPage;

